import {
  setDestinationPosition,
  setNavigationRoute,
  setNavigationStatus,
} from "../navigation/navigationSlice";

import store from "@/app/store";
import {
  type Building,
  CURRENT_USER_LOCATION_ID,
  FIND_MY_CAR_LOCATION_ID,
  NavigationStatus,
  type Poi,
  Position,
} from "@/libs/models";
import { getSDKEndpoints } from "../directions/actions";
import { mapFindMyCarToPosition } from "../directions/mapper";
import { RouteType } from "../directions/types";
import { handleNavigationError } from "../navigation/actions";
import {
  type NativeNavigationProgressPayload,
  type NativeNavigationStatusPayload,
  type NativeNavigationUpdatedPayload,
  type NativeNavigationUpdatedPayloadType,
  NativeNavigationUpdateTypes,
  type RouteNode,
  type RouteSegment,
} from "../navigation/types";
import { mapPoiToSDK, mapWASMRouteToSDK } from "../utils/sdkMapper";
import { calculateTimeWalking } from "../utils/utils";
import { WebviewApi } from "../webview_api";
import type { WASMRoutesCalculator } from "./routeCalculatorWASM";

/**
 * Initiates the navigation, this is called whenever the
 * user starts a new navigation
 */
export const computeInitialWASMNavigation = ({
  routesCalculator,
  navigationFrom,
  navigationTo,
  type,
  notifyNavigationStart = true,
}: {
  routesCalculator: WASMRoutesCalculator;
  navigationFrom: number;
  navigationTo: number | Position;
  type: RouteType;
  notifyNavigationStart?: boolean;
}) => {
  const { origin, destination } = getNativeEndpoints(
    navigationFrom,
    navigationTo,
  );

  store.dispatch(
    setDestinationPosition(
      destination instanceof Position ? destination : null,
    ),
  );
  routesCalculator.setNavigationParametersRoute().then(() => {
    routesCalculator
      .calculateRoute(origin, destination, type)
      .then((result) => {
        if (result.payload?.error) {
          console.error(
            "Error occurred while starting native navigation:",
            result.payload.error,
          );
          handleNavigationError();
          return;
        }
        updateNativeNavigationRoute(
          routesCalculator,
          true,
          notifyNavigationStart,
        );
      });
  });
};

/**
 * Responsible for updating the state of the navigation
 * both when the navigation starts and whenever the location
 * changes
 */
export const updateNativeNavigationRoute = async (
  routesCalculator: WASMRoutesCalculator | null,
  isFirst = false,
  notifyNavigationStart = true,
) => {
  if (
    !routesCalculator ||
    store.getState().navigation.navigationStatus == NavigationStatus.FINISHED
  ) {
    return;
  }

  if (isFirst) {
    await routesCalculator.setNavigationParametersRoute();
  }
  routesCalculator.calculateNativeNavigation().then((result) => {
    if (result.payload?.error) {
      console.error(result.payload.error);
      handleNavigationError();
      return;
    }
    const navigationResult = result.payload;
    handleNavigationUpdate(
      routesCalculator,
      navigationResult,
      isFirst,
      notifyNavigationStart,
    );
  });
};

const handleNavigationUpdate = (
  routesCalculator: WASMRoutesCalculator,
  navigationResult: any,
  isFirst = false,
  notifyNavigationStart = true,
) => {
  const indications = navigationResult?.indications;
  const innerIndications = indications?.indications;
  const firstIndication = innerIndications?.length > 0 && innerIndications[0];

  // OOR
  if (firstIndication?.["user out of route"] === true) {
    handleNativeNavigationOOR(routesCalculator);
    return;
  }

  // Finished
  if (firstIndication?.indicationType == "END") {
    handleNavigationDestinationReached(navigationResult);
    return;
  }

  const navigationStatus = store.getState().navigation.navigationStatus;
  // If no navigation started ignore updates
  if (navigationStatus === NavigationStatus.STOPPED) {
    return;
  }

  // Update
  if (indications?.segments) {
    handleNativeNavigationProgress(
      navigationResult,
      isFirst,
      notifyNavigationStart,
    );
  }
};

/**
 * Tells the library to compute a new route and obtains the updated navigation
 * information
 */
const reCalculateNativeNavigation = async (
  routesCalculator: WASMRoutesCalculator | null,
) => {
  if (routesCalculator) {
    const { origin, destination } = getNativeEndpoints(
      CURRENT_USER_LOCATION_ID,
      store.getState().navigation.destinationPosition ??
        store.getState().navigation.navigationTo,
    );
    const routeType =
      store.getState().navigation.tags[0] ||
      store.getState().config.directions.defaultRouteType;

    return routesCalculator
      .calculateRoute(origin, destination, routeType)
      .then(async ({ payload }) => {
        await routesCalculator.setNavigationParametersRoute();
        // On error try with default route type
        if (payload?.error) {
          const routeType = store.getState().config.directions.defaultRouteType;
          return routesCalculator
            .calculateRoute(origin, destination, routeType)
            .then(async ({ payload }) => {
              await routesCalculator.setNavigationParametersRoute();
              if (payload?.error) {
                console.error(
                  `Error updating route indications when recalculating the route: ${payload.error}`,
                );
                handleNavigationError();
                return null;
              }
              return routesCalculator.calculateNativeNavigation();
            });
        }
        return routesCalculator.calculateNativeNavigation();
      });
  }
};

/**
 * Callback for when user goes out of route
 */
const handleNativeNavigationOOR = (routesCalculator: WASMRoutesCalculator) => {
  // Notify OOR
  WebviewApi.nativeNavigationUpdated(NativeNavigationUpdateTypes.OOR);
  reCalculateNativeNavigation(routesCalculator).then((result) => {
    if (!result) return;
    const { payload: navigationResult } = result;
    handleNavigationUpdate(routesCalculator, navigationResult, true);
  });
};

/**
 * Callback for when navigation finishes
 */
const handleNavigationDestinationReached = (navigationResult: any) => {
  store.dispatch(setNavigationRoute(navigationResult?.indications));
  store.dispatch(setNavigationStatus(NavigationStatus.FINISHED));
  WebviewApi.nativeNavigationUpdated(
    NativeNavigationUpdateTypes.DESTINATION_REACHED,
  );
};

/**
 * Callback for when navigation route information is updated
 */
const handleNativeNavigationProgress = (
  navigationResult: any,
  isFirst: boolean,
  notifyNavigationStart: boolean,
) => {
  //TODO substituir path por indications?
  store.dispatch(setNavigationRoute(navigationResult.indications));
  // needed to show AR button on WASM
  store.dispatch(setNavigationStatus(NavigationStatus.PROGRESS));
  if (isFirst) {
    if (notifyNavigationStart) {
      WebviewApi.nativeNavigationStarted();
    }
  } else {
    WebviewApi.nativeNavigationUpdated(NativeNavigationUpdateTypes.PROGRESS);
  }
};

/**
 * Maps an poi identifier to a position. If the input
 * is a position it simply returns the position itself.
 */
const mapToNativeEndpoint = (
  endpoint: Poi["id"] | Position,
  currentBuilding: Building,
) => {
  // Custom endpoint
  if (endpoint instanceof Position) {
    return endpoint;
  } else if (endpoint == CURRENT_USER_LOCATION_ID) {
    return store.getState().location.currentLocation?.toPosition();
  } else if (endpoint == FIND_MY_CAR_LOCATION_ID) {
    return mapFindMyCarToPosition(store.getState().findMyCar, currentBuilding);
  } else {
    return currentBuilding.pois?.getPoiById(endpoint)?.id;
  }
};

/**
 * Obtains origin and destination endpoints for WASM routes. These can either
 * be positions (i.e. current location) or ids (a poi)
 */
export const getNativeEndpoints = (
  origin?: Poi["id"] | null,
  destination?: Poi["id"] | Position | null,
): {
  origin?: number | Position | null;
  destination?: number | Position | null;
} => {
  const currentBuilding = Object.values(store.getState().cartography.buildings)
    .map((b) => b.value)
    .find((b) => b.id == store.getState().cartography.currentBuildingID);

  return {
    destination:
      destination && mapToNativeEndpoint(destination, currentBuilding),
    origin: origin && mapToNativeEndpoint(origin, currentBuilding),
  };
};

export const mapNativeNavigationStatusPayload = ():
  | NativeNavigationStatusPayload
  | undefined => {
  const route = mapWASMRouteToSDK(store.getState().navigation.route);
  const routeType = store.getState().navigation.tags[0];
  const currentBuildingId = store.getState().cartography.currentBuildingID;

  const points: RouteNode[] | undefined = route?.segments?.reduce(
    (acc: RouteNode[], s: RouteSegment) => {
      acc.push(...(s.points as RouteNode[]));
      return acc;
    },
    [],
  );

  const destinationPoiId = store.getState().navigation.navigationTo;
  // This will have a position stored when destination is either find my car
  // or a custom destination
  const destinationPosition = store.getState().navigation.destinationPosition;

  const { destination: poiDestination } = getSDKEndpoints({
    destination: destinationPosition ?? destinationPoiId,
  });

  if (!poiDestination) {
    return;
  }

  const currentCategories = store
    ?.getState()
    ?.app?.poiCategories?.filterByIds(poiDestination.categoryIds);

  const sdkPoiTo = mapPoiToSDK(poiDestination, currentCategories);

  if (
    !route ||
    !route.indications ||
    !points ||
    !route.steps ||
    !route.segments ||
    !currentBuildingId ||
    !sdkPoiTo
  ) {
    return;
  }

  const payload: NativeNavigationStatusPayload = {
    accessibilityMode: routeType ?? RouteType.CHOOSE_SHORTEST,
    buildingIdentifier: currentBuildingId,
    indications: route?.indications,
    points: points,
    poiTo: sdkPoiTo,
    segments: route?.segments,
    steps: route?.steps,
  };

  return payload;
};

/**
 * Called from viewer.navigation.updated invocation
 */
export const mapNativeNavigationUpdatePayload = (
  type: NativeNavigationUpdateTypes,
): NativeNavigationUpdatedPayloadType<"STATUS" | "PROGRESS"> | undefined => {
  if (type == NativeNavigationUpdateTypes.PROGRESS) {
    const value = mapNativeNavigationProgressPayload();
    if (!value) return;

    return {
      type,
      ...value,
    } as NativeNavigationUpdatedPayload<"PROGRESS">;
  }

  const value = mapNativeNavigationStatusPayload();
  if (!value) return;

  return {
    type,
    ...value,
  } as NativeNavigationUpdatedPayload<"STATUS">;
};

/**
 * Called from mapNativeNavigationUpdatePayload(PROGRESS)
 */
const mapNativeNavigationProgressPayload = ():
  | NativeNavigationProgressPayload
  | undefined => {
  const route = mapWASMRouteToSDK(store.getState().navigation.route);

  const currentIndication = route?.indications.at(0);
  const nexIndication = route?.indications.at(1);

  const points: RouteNode[] | undefined = route?.segments?.reduce(
    (acc: RouteNode[], s: RouteSegment) => {
      acc.push(...(s.points as RouteNode[]));
      return acc;
    },
    [],
  );

  if (
    !route ||
    !points ||
    !route.currentStep ||
    !currentIndication ||
    !route.segments
  )
    return;

  const time = calculateTimeWalking(
    Math.ceil(route.currentStep?.distanceToGoal) ?? 0,
  );

  const payload: NativeNavigationProgressPayload = {
    currentIndication: currentIndication,
    currentStepIndex: route.steps.findIndex(
      (step) => step.id == route.currentStep?.id,
    ),
    distanceToClosestPointInRoute: 0, //<--- existe en core pero no se nos pasa
    distanceToEndStep: currentIndication.distance,
    distanceToGoal: route.currentStep.distanceToGoal,
    nextIndication: nexIndication || currentIndication,
    points: points,
    routeStep: route.currentStep,
    segments: route.segments,
    timeToEndStep: 0, //<--- no existe ni en core ni en web
    timeToGoal: time,
  };
  return payload;
};

export const mapCanceledPayload =
  (): NativeNavigationUpdatedPayloadType<"STATUS"> => {
    const type = NativeNavigationUpdateTypes.CANCELLED;
    return {
      type,
    } as NativeNavigationUpdatedPayload<"STATUS">;
  };
